import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import SidebarToggleButton from "../components/SidebarToggleButton";
import IntroSection from "../components/IntroSection";
import IntroSectionSkeleton from "../components/IntroSectionSkeleton";
import { api } from "../lib/api";
import { useIntro } from "../hooks/useIntro";
import { Database, Lock, ArrowRight, Network } from "lucide-react";

// Paste ANY of these into videoUrl and IntroSection auto-picks the renderer:
//   • YouTube share URL         "https://youtu.be/XXXXXXX"
//   • YouTube embed URL         "https://www.youtube.com/embed/XXXXXXX"
//   • Vimeo player URL          "https://player.vimeo.com/video/XXXXXXX"
//   • Direct MP4 / WEBM / MOV   "https://cdn.example.com/intro.mp4"
export const DATABASES_INTRO = {
  eyebrow: "Databases Overview",
  title: "Hand-vetted rolodexes, in one place.",
  body: "Buyers, distributors, brokers, co-packers, agencies, investors, and trade media — every list is curated by the Institute team and updated on a monthly cadence. Watch the walkthrough, then click into whichever database matches the door you're trying to open.",
  videoUrl: "",
  bullets: [
    "Search, filter, and save any row to a personal list",
    "Warm-intro notes on every contact where we have them",
    "New rows added monthly · flag anything stale in one click",
  ],
};

export default function DatabasesIndex() {
  const [dbs, setDbs] = useState([]);
  const { intro, ready: introReady } = useIntro("databases", DATABASES_INTRO);

  useEffect(() => {
    api.get("/databases").then((r) => setDbs(r.data)).catch(() => {});
  }, []);

  // Dynamic counts — Industry Directory is a separate feature (its own page
  // + its own router + its own data model); it is NOT a row of
  // `db.databases` and is intentionally excluded from these counts.
  const total = dbs.reduce((sum, d) => sum + (d.rows || 0), 0);
  const unlocked = dbs.filter((d) => !d.locked).length;
  const listCount = dbs.length;

  return (
    <AppShell title="Databases">
      <div className="w-full max-w-none">
        {introReady && intro ? (
          <IntroSection
            {...intro}
            ctaLabel="Browse the directories"
            ctaHref="#databases-grid"
            allowSeek
            configHint={<>Admin → Intros to edit copy + video. Fallback in <code>src/pages/DatabasesIndex.jsx</code>.</>}
            testId="databases-intro"
          />
        ) : (
          <IntroSectionSkeleton testId="databases-intro" />
        )}

        <div id="databases-grid" className="mt-12">
          <PageHero
            eyebrow="Connect"
            icon={Network}
            title={<>The Institute <span className="text-grad">Databases</span></>}
            subtitle={`${listCount} vetted directories covering every partner category you'll interact with — product developers, retailers, distributors, brokers, financing, freight, excess inventory, investors, co-packers, and more.`}
            right={<SidebarToggleButton testId="dbs-sidebar-toggle" />}
          />

          <div className="grid sm:grid-cols-3 gap-4">
            <Stat label="Databases" value={listCount} testid="dbs-count-databases" />
            <Stat label="Total records" value={total.toLocaleString()} testid="dbs-count-records" />
            <Stat label="Unlocked on your plan" value={`${unlocked} / ${listCount}`} testid="dbs-count-unlocked" />
          </div>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {dbs.map((d) => (
            <Link
              key={d.slug}
              to={`/databases/${d.slug}`}
              data-testid={`database-card-${d.slug}`}
              className={["surface p-6 flex flex-col",
                d.locked ? "opacity-80" : "hover:-translate-y-0.5 transition-transform"].join(" ")}
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-shell-accent/20 grid place-items-center">
                  <Database className="w-5 h-5 text-shell-side" />
                </div>
                {d.locked ? (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-secondary text-white px-2 py-1 rounded-full">
                    <Lock className="w-3 h-3" /> Locked
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest bg-muted text-muted-foreground px-2 py-1 rounded-full font-semibold">
                    {(d.rows || 0).toLocaleString()} rows
                  </span>
                )}
              </div>
              <h3 className="font-display text-2xl mt-4">{d.name}</h3>
              <p className="text-sm text-muted-foreground mt-2 flex-1">{d.description}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-xs font-semibold text-shell-side">
                {d.locked ? "Upgrade to unlock" : "Open database"} <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="surface p-5" data-testid={testid}>
      <p className="font-display text-3xl">{value}</p>
      <p className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}
